import dayjs from "dayjs";
import { formatCurrencyAmount } from "../../utils/currency";
import {
  ApiNotification,
  ApiNotificationType,
  NotificationAction,
  NotificationCategory,
  NotificationItem,
  NotificationMetadata,
  NotificationStatus,
  NotificationType,
  ReceiptKind,
} from "./types";

const PAYMENT_LINK_FAILED_TYPES = new Set<string>([
  "PAYMENT_LINK_CREATE_FAILED",
  "PAYMENT_LINK_DELETE_FAILED",
  "PAYMENT_LINK_SHARE_FAILED",
]);

const RECEIPT_PAYOUT_TYPES = new Set<string>([
  "MERCHANT_PAYOUT_SUCCESSFUL",
  "MERCHANT_PAYOUT_FAILED",
  "MERCHANT_OTC_PAYOUT_SUCCESSFUL",
  "MERCHANT_OTC_PAYOUT_FAILED",
]);

const RECEIPT_BANK_TRANSFER_TYPES = new Set<string>([
  "BANK_TRANSFER_PAYOUT_SUCCESSFUL",
  "BANK_TRANSFER_PAYOUT_FAILED",
]);

const RECEIPT_KLASHA_WIRE_TYPES = new Set<string>([
  "KLASHA_WIRE_SUCCESSFUL",
  "KLASHA_WIRE_FAILED",
]);

export const parseNotificationMetadata = (
  metadata: string
): NotificationMetadata => {
  if (!metadata) {
    return {};
  }

  try {
    return JSON.parse(metadata) as NotificationMetadata;
  } catch {
    return {};
  }
};

export const formatNotificationTimeAgo = (createdAt: string) => {
  const created = dayjs(createdAt);
  const now = dayjs();
  const diffMinutes = now.diff(created, "minute");
  const diffHours = now.diff(created, "hour");
  const diffDays = now.diff(created, "day");

  if (diffMinutes < 1) {
    return "a few seconds ago";
  }

  if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes === 1 ? "" : "s"} ago`;
  }

  if (diffHours < 24 && created.isSame(now, "day")) {
    return `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
  }

  if (diffDays === 1 || created.isSame(now.subtract(1, "day"), "day")) {
    return "Yesterday";
  }

  return `${diffDays} days ago`;
};

export const getNotificationType = (apiType: string): NotificationType => {
  if (PAYMENT_LINK_FAILED_TYPES.has(apiType)) {
    return "payment_link_failed";
  }

  if (apiType.startsWith("PAYMENT_LINK_")) {
    return "payment_link_success";
  }

  switch (apiType as ApiNotificationType) {
    case "MERCHANT_PAYOUT_SUCCESSFUL":
    case "MERCHANT_OTC_PAYOUT_SUCCESSFUL":
    case "BANK_TRANSFER_PAYOUT_SUCCESSFUL":
      return "payout_completed";
    case "MERCHANT_PAYOUT_FAILED":
    case "MERCHANT_OTC_PAYOUT_FAILED":
    case "BANK_TRANSFER_PAYOUT_FAILED":
      return "payout_failed";
    case "KLASHA_WIRE_SUCCESSFUL":
      return "wire_completed";
    case "KLASHA_WIRE_FAILED":
      return "wire_failed";
    case "VA_SETTLEMENT_COMPLETED":
      return "settlement_completed";
    case "WITHDRAW_REQUEST_CREATED":
    case "BANK_TRANSFER_REQUEST_CREATED":
    case "FOREIGN_BANK_TRANSFER_REQUEST_CREATED":
      return "transaction_pending";
    case "WITHDRAW_REQUEST_CREATE_FAILED":
    case "BANK_TRANSFER_REQUEST_CREATE_FAILED":
    case "FOREIGN_BANK_TRANSFER_REQUEST_CREATE_FAILED":
      return "transaction_failed";
    default:
      break;
  }

  if (apiType.includes("FAILED")) {
    return "transaction_failed";
  }

  return "transaction_completed";
};

export const getNotificationCategory = (
  apiType: string
): NotificationCategory => {
  if (apiType.startsWith("PAYMENT_LINK_")) {
    return "Payment Links";
  }

  if (apiType.startsWith("WITHDRAW_REQUEST_")) {
    return "Collections";
  }

  if (apiType.startsWith("VA_SETTLEMENT_")) {
    return "Settlements";
  }

  if (apiType.startsWith("KLASHA_WIRE_")) {
    return "Wire";
  }

  if (
    apiType.includes("PAYOUT") ||
    apiType.includes("BANK_TRANSFER") ||
    apiType.includes("FOREIGN_BANK")
  ) {
    return "Payouts";
  }

  return "Transactions";
};

export const getNotificationStatus = (
  apiType: string
): NotificationStatus => {
  if (
    PAYMENT_LINK_FAILED_TYPES.has(apiType) ||
    apiType.includes("FAILED")
  ) {
    return "failed";
  }

  const isRequestCreationType =
    apiType === "WITHDRAW_REQUEST_CREATED" ||
    apiType === "BANK_TRANSFER_REQUEST_CREATED" ||
    apiType === "FOREIGN_BANK_TRANSFER_REQUEST_CREATED";

  if (
    isRequestCreationType ||
    apiType.includes("PENDING") ||
    apiType.includes("PROCESSING")
  ) {
    return "pending";
  }

  if (apiType.includes("SETTLEMENT") || apiType.includes("COLLECTION")) {
    return "received";
  }

  return "successful";
};

const getNotificationActions = (
  apiType: string,
  metadata: NotificationMetadata
): NotificationAction[] => {
  if (
    RECEIPT_PAYOUT_TYPES.has(apiType) ||
    RECEIPT_BANK_TRANSFER_TYPES.has(apiType) ||
    RECEIPT_KLASHA_WIRE_TYPES.has(apiType)
  ) {
    return ["download_receipt"];
  }

  if (apiType.startsWith("PAYMENT_LINK_") && metadata.paymentLinkId) {
    return ["view_transaction"];
  }

  return [];
};

const getAmountDisplay = (metadata: NotificationMetadata) => {
  if (metadata.amount != null) {
    return {
      amount: formatCurrencyAmount(metadata.currency, metadata.amount),
      currency: metadata.currency,
    };
  }

  if (metadata.sourceAmount != null) {
    return {
      amount: formatCurrencyAmount(
        metadata.sourceCurrency,
        metadata.sourceAmount
      ),
      currency: metadata.sourceCurrency,
    };
  }

  return { amount: undefined, currency: undefined };
};

const getReference = (
  apiType: string,
  metadata: NotificationMetadata
): string | undefined => {
  if (metadata.paymentLinkId) {
    return `PL-${metadata.paymentLinkId}`;
  }

  if (metadata.requestId) {
    return metadata.requestId;
  }

  if (metadata.reference) {
    return metadata.reference;
  }

  if (metadata.settlementReference) {
    return metadata.settlementReference;
  }

  if (metadata.transactionRef) {
    return metadata.transactionRef;
  }

  if (RECEIPT_KLASHA_WIRE_TYPES.has(apiType) && metadata.id != null) {
    return `KW-${metadata.id}`;
  }

  return undefined;
};

const getSource = (
  apiType: string,
  metadata: NotificationMetadata
): string | undefined => {
  if (metadata.sharedVia) {
    return `Shared via ${metadata.sharedVia}`;
  }

  if (metadata.error) {
    return undefined;
  }

  if (metadata.paymentLinkId) {
    return `Payment link · #${metadata.paymentLinkId}`;
  }

  if (metadata.bankName && metadata.accountNumber) {
    return `${metadata.bankName} · ${metadata.accountNumber}`;
  }

  if (metadata.bankName) {
    return metadata.bankName;
  }

  if (apiType.startsWith("VA_SETTLEMENT_") && metadata.currency) {
    return `${metadata.currency} Virtual Account`;
  }

  return undefined;
};

export const getReceiptKind = (apiType: string): ReceiptKind | null => {
  if (RECEIPT_PAYOUT_TYPES.has(apiType)) {
    return "payout";
  }

  if (RECEIPT_BANK_TRANSFER_TYPES.has(apiType)) {
    return "bank-transfer";
  }

  if (RECEIPT_KLASHA_WIRE_TYPES.has(apiType)) {
    return "klasha-wire";
  }

  return null;
};

export const getReceiptLookupId = (
  apiType: string,
  metadata: NotificationMetadata
): string | null => {
  const kind = getReceiptKind(apiType);

  switch (kind) {
    case "payout":
    case "bank-transfer":
      return metadata.requestId ? String(metadata.requestId) : null;
    case "klasha-wire":
      return metadata.id != null ? String(metadata.id) : null;
    case null:
      return null;
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
};

export const mapApiNotification = (
  notification: ApiNotification
): NotificationItem => {
  const metadata = parseNotificationMetadata(notification.metadata);
  const type = getNotificationType(notification.type);
  const { amount, currency } = getAmountDisplay(metadata);

  return {
    id: String(notification.id),
    apiType: notification.type,
    type,
    title: notification.title,
    message: notification.message,
    description: notification.message,
    amount,
    currency,
    status: getNotificationStatus(notification.type),
    timeAgo: formatNotificationTimeAgo(notification.createdAt),
    category: getNotificationCategory(notification.type),
    reference: getReference(notification.type, metadata),
    read: notification.read,
    actions: getNotificationActions(notification.type, metadata),
    createdAt: notification.createdAt,
    metadata,
    source: getSource(notification.type, metadata),
  };
};

export const mapApiNotifications = (
  notifications: ApiNotification[]
): NotificationItem[] => notifications.map(mapApiNotification);

export const groupNotificationsByDate = (items: NotificationItem[]) => {
  const groups: { label: string; items: NotificationItem[] }[] = [];

  items.forEach((item) => {
    const created = dayjs(item.createdAt);
    const label = created.isSame(dayjs(), "day") ? "Today" : "Earlier";

    const existing = groups.find((group) => group.label === label);
    if (existing) {
      existing.items.push(item);
      return;
    }

    groups.push({ label, items: [item] });
  });

  return groups;
};
